// ============================================================================
// CHARGE TYPE MIX WIDGET (DASHBOARD)
// ============================================================================
// Pie chart showing the bifurcation of total logged hours by Charge Type
// (Chargeable / NCP / NCNP / Disbursement).
//
// Data source: re-uses the existing /Reports/employee-activity endpoint with
// groupBy=ChargeType, scoped to the same week + manager filter the dashboard
// is using. No new backend surface required.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EChart } from 'components/ui/EChart';
import { reportService } from 'api/services/reportService';
import type { EChartsCoreOption } from 'echarts/core';
import type { EmployeeActivityReportRow } from 'api/types/report.types';

interface Props {
  managerId?: string;
  /** ISO date (YYYY-MM-DD) of the selected week start. */
  weekStartDate?: string;
}

const CHARGE_TYPE_COLORS: Record<string, string> = {
  C:    '#16a34a', // Chargeable — green
  NCP:  '#0ea5e9', // Non-Chargeable Productive — blue
  NCNP: '#94a3b8', // Non-Chargeable Non-Productive — slate
  D:    '#d97706', // Disbursement — amber
};

const CHARGE_TYPE_LABEL: Record<string, string> = {
  C: 'Chargeable',
  NCP: 'NCP',
  NCNP: 'NCNP',
  D: 'Disbursement',
};

/** Compute the week-end date (weekStart + 6 days) as YYYY-MM-DD. */
function computeWeekEnd(weekStartDate: string): string {
  const [y, m, d] = weekStartDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

const ChargeTypeMixWidget: React.FC<Props> = ({ managerId, weekStartDate }) => {
  const [rows, setRows] = useState<EmployeeActivityReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!weekStartDate) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    reportService
      .getEmployeeActivity({
        groupBy: 'ChargeType',
        fromDate: weekStartDate,
        toDate: computeWeekEnd(weekStartDate),
        managerId: managerId || undefined,
        page: 1,
        pageSize: 10,
      })
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [managerId, weekStartDate]);

  const chartData = useMemo(
    () =>
      rows
        .filter((r) => Number(r.totalHours || 0) > 0)
        .map((r) => {
          const code = (r.chargeTypeKey || '').toUpperCase();
          const label = CHARGE_TYPE_LABEL[code] || r.chargeTypeValue || code || '—';
          return {
            name: label,
            value: Number(r.totalHours || 0),
            code,
            itemStyle: { color: CHARGE_TYPE_COLORS[code] || '#64748b' },
          };
        }),
    [rows],
  );

  const totalHours = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData],
  );

  const option: EChartsCoreOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => `${p.name}: <b>${Number(p.value).toFixed(2)} h</b> (${p.percent}%)`,
      },
      legend: {
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { fontSize: 11 },
      },
      series: [
        {
          name: 'Hours by Charge Type',
          type: 'pie',
          radius: ['45%', '72%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: 'transparent',
            borderWidth: 2,
          },
          label: { show: false },
          emphasis: {
            scale: true,
            scaleSize: 6,
            label: { show: true, fontSize: 12, fontWeight: 'bold' },
          },
          data: chartData,
        },
      ],
    }),
    [chartData],
  );

  // Tooltip describing what is and isn't included in the pie. Surfaces
  // the rules so users don't have to guess (this came up in review — the
  // chart only counts direct reportees, includes Draft+Submitted, and
  // excludes the manager's own entries).
  const tooltipText = [
    'Includes: timesheet entries with status Draft or Submitted',
    '  (Not Started / deleted timesheets are excluded)',
    'Scope: direct reportees of the selected manager',
    '  (manager\'s own entries and sub-managers\' teams are NOT included)',
    'Period: the week chosen in the dashboard period selector',
  ].join('\n');

  return (
    <div className="dashboard-widget">
      <div className="dashboard-widget-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.chart size={18} />
          <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>
            Hours by Charge Type
          </h3>
          <span
            title={tooltipText}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'help',
              color: 'var(--text-muted)',
            }}
            aria-label="What's included in this widget"
          >
            <IconSystem.info size={14} />
          </span>
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Total: <strong>{totalHours.toFixed(2)} h</strong>
        </span>
      </div>
      {!loading && chartData.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          No hours logged for this period.
        </div>
      ) : (
        <EChart option={option} height={260} loading={loading} />
      )}
    </div>
  );
};

export default ChargeTypeMixWidget;
